import { IAuthRepositoryCB } from "../interfaces/repository/IAuthRepository";
import { api } from "services/api/Api";
import { deviceConfigApi } from "services/api/equipamento/DeviceConfigApi";

// Cabeçalhos com que o servidor devolve a sessão renovada. Enquanto a renovação estiver
// desligada lá, eles simplesmente não vêm e nada aqui acontece.
export const RENEWED_TOKEN_HEADER = "x-meep-access-token";
export const RENEWED_TOKEN_EXPIRES_IN_HEADER = "x-meep-access-token-expires-in";

// Disparado depois de cada troca, com o token novo em "detail". Os iframes que recebem a sessão
// por postMessage só a recebem ao carregar; é por este evento que ficam sabendo da renovação.
export const RENEWED_TOKEN_EVENT = "meep:token-renovado";

// O login também grava a resposta inteira em "@token", e menu, notificações, faturamento,
// catálogo, fiscal e tutoriais leem o token dali direto. Sem trocar esta cópia, essas chamadas
// seguiriam com o token do login e falhariam quando ele vencesse, com a sessão já renovada.
const LOGIN_RESPONSE_STORAGE_KEY = "@token";

const updateLoginResponseCopy = (accessToken: string, expiresIn: number) => {
  const stored = localStorage.getItem(LOGIN_RESPONSE_STORAGE_KEY);
  if (!stored) return;

  try {
    const loginResponse = JSON.parse(stored);
    localStorage.setItem(
      LOGIN_RESPONSE_STORAGE_KEY,
      JSON.stringify({
        ...loginResponse,
        access_token: accessToken,
        ...(Number.isFinite(expiresIn) && expiresIn > 0 ? { expires_in: expiresIn } : {}),
      })
    );
  } catch {
    // cópia ilegível: não é a renovação que vai consertá-la
  }
};

/**
 * Troca o token guardado pelo que veio na resposta, sem mexer no resto da sessão: o usuário,
 * os papéis e os locais continuam os mesmos, porque o servidor reemite com a MESMA identity.
 *
 * Devolve true só quando houve troca — é o que permite ao chamador não fazer nada no caso
 * comum, que é a resposta não trazer cabeçalho nenhum.
 */
export const RenewAuthTokenUseCase = (
  accessToken: string | undefined,
  expiresIn: string | number | undefined,
  _authRepository: IAuthRepositoryCB
): boolean => {
  if (!accessToken) return false;

  const authRepository = _authRepository();
  const auth = authRepository.get();

  // Sem sessão guardada não há o que renovar: é o caso de quem acabou de deslogar e ainda
  // tem uma requisição em voo. Gravar aqui ressuscitaria a sessão que o logout apagou.
  if (!auth) return false;
  if (auth.access_token === accessToken) return false;

  const parsedExpiresIn = Number(expiresIn);

  authRepository.set({
    ...auth,
    access_token: accessToken,
    expires_in: Number.isFinite(parsedExpiresIn) && parsedExpiresIn > 0 ? parsedExpiresIn : auth.expires_in,
  });
  updateLoginResponseCopy(accessToken, parsedExpiresIn);

  api.defaults.headers.Authorization = `Bearer ${accessToken}`;
  deviceConfigApi.defaults.headers.Authorization = `Bearer ${accessToken}`;

  window.dispatchEvent(new CustomEvent<string>(RENEWED_TOKEN_EVENT, { detail: accessToken }));

  return true;
};
